import React from 'react';
import {
  Drawer,
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { PeopleFilters } from '../people/PeopleFilters';
import { PeopleFilters as PeopleFiltersType } from '../../hooks/usePeopleFilters';

/**
 * SearchFiltersPanel Component
 *
 * Filter panel for search results
 *
 * Features:
 * - Desktop: Fixed sidebar on left
 * - Mobile: Drawer that slides from right
 * - Reuses PeopleFilters component for consistency
 * - Apply/Clear filters buttons
 *
 * @component
 * @param {SearchFiltersPanelProps} props - Component props
 * @returns {JSX.Element} Search filters panel
 */
interface SearchFiltersPanelProps {
  open: boolean;
  onClose: () => void;
  filters: PeopleFiltersType;
  onFiltersChange: (filters: PeopleFiltersType) => void;
  isMobile: boolean;
}

export const SearchFiltersPanel: React.FC<SearchFiltersPanelProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  isMobile,
}) => {
  // ===== Event Handlers =====
  const handleClearFilters = () => {
    onFiltersChange({});
  };

  // ===== Render Helper =====
  const renderFilterContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header (Mobile only) */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Divider />
        </Box>
      )}

      {/* Filter Controls */}
      <Box sx={{ flex: 1, overflow: 'auto', ...(isMobile ? { p: 3 } : {}) }}>
        <PeopleFilters
          filters={filters}
          onChange={onFiltersChange}
          persons={[]} // Empty array - we don't need lastName dropdown for search
        />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          ...(isMobile && {
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }),
          ...(!isMobile && {
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }),
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={handleClearFilters}
        >
          Clear All Filters
        </Button>
        {isMobile && (
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
          >
            Apply
          </Button>
        )}
      </Box>
    </Box>
  );

  // ===== Render =====
  if (isMobile) {
    // Mobile: Drawer
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '90%', sm: 400 },
            maxWidth: '100%',
          },
        }}
      >
        {renderFilterContent()}
      </Drawer>
    );
  }

  // Desktop: Fixed sidebar (only show when open)
  if (!open) {
    return null;
  }

  return (
    <Paper
      sx={{
        width: 280,
        flexShrink: 0,
        p: 3,
        height: 'fit-content',
        position: 'sticky',
        top: 80, // Account for AppHeader height
      }}
    >
      {renderFilterContent()}
    </Paper>
  );
};
